import { Box, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { AppliedFieldMapping } from "../../hooks/useAppliedFieldMapping";
import type { SampleValueLookup } from "./buildSampleValueLookup";

interface AppliedMappingTableProps {
  mappings: AppliedFieldMapping[];
  /** Optional `targetField → sample value` map. When provided, the table
   *  renders an extra "Sample (row 1)" column so reviewers can see *what*
   *  value came out of the mapping for the first loaded record. When
   *  omitted (e.g. for UPLOADED docs with no completed load yet), the
   *  table renders just Detected Header → Mapped Field. */
  sampleValueLookup?: SampleValueLookup;
}

/**
 * Header → Field mapping table used by `SchemaMappingCard`. Renders 2 or 3
 * columns depending on whether a sample-value lookup is supplied. Field
 * descriptions render as a tooltip on the mapped-field cell rather than
 * inline italic body — fixes the "vertical noise" complaint that motivated
 * the mapping-tab rework.
 */
export const AppliedMappingTable = ({ mappings, sampleValueLookup }: AppliedMappingTableProps) => {
  const showSample = !!sampleValueLookup;

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: "bold", width: showSample ? "30%" : "40%" }}>Detected Header</TableCell>
          <TableCell sx={{ width: "20px" }} />
          <TableCell sx={{ fontWeight: "bold", width: showSample ? "35%" : undefined }}>Mapped To Field</TableCell>
          {showSample && <TableCell sx={{ fontWeight: "bold" }}>Sample (row 1)</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {mappings.map((mapping) => (
          <TableRow key={`${mapping.detectedHeader}-${mapping.targetField}`}>
            <TableCell>
              <Typography variant="body2" fontWeight="medium">
                {mapping.detectedHeader}
              </Typography>
            </TableCell>
            <TableCell>
              <ArrowForwardIcon fontSize="small" sx={{ color: "text.secondary", verticalAlign: "middle" }} />
            </TableCell>
            <TableCell>
              <MappedFieldCell mapping={mapping} />
            </TableCell>
            {showSample && (
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {sampleValueLookup[mapping.targetField] ?? "—"}
                </Typography>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

interface MappedFieldCellProps {
  mapping: AppliedFieldMapping;
}

/** Mapped-field cell content. Field description (when present) and the raw
 *  target_field name both live in a tooltip on hover — moved off the inline
 *  italic body to keep rows compact and match the polished feel of the
 *  Result tab. */
const MappedFieldCell = ({ mapping }: MappedFieldCellProps) => {
  const tooltipParts: string[] = [`Field: ${mapping.targetField}`];
  if (mapping.fieldDescription) tooltipParts.push(mapping.fieldDescription);
  const tooltipBody = tooltipParts.join("\n\n");

  return (
    <Tooltip title={<Box sx={{ whiteSpace: "pre-line", py: 0.25 }}>{tooltipBody}</Box>} placement="left" arrow>
      <Typography variant="body2" sx={{ cursor: "help", display: "inline-block" }}>
        {mapping.fieldLabel}
      </Typography>
    </Tooltip>
  );
};
